import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import * as ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  FiUpload,
  FiDownload,
  FiFile,
  FiUsers,
  FiChevronLeft,
  FiChevronsLeft,
  FiChevronRight,
  FiChevronsRight,
  FiSearch,
  FiRefreshCw,
  FiX,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiGrid,
  FiList,
} from "react-icons/fi";
import {
  createOffense,
  fetchOffenses,
  updateOffense,
  deleteOffense,
  downloadTemplate,
  uploadOffense,
} from "../../../redux/actions/survey-management/offenseMasterActions";
import {
  fetchDistricts,
  fetchMunicipalities,
  clearMunicipalities,
} from "../../../redux/actions/internal/internalActions";

const OffenseMaster = () => {
  const dispatch = useDispatch();
  const { loading, offenses, pagination, uploadProgress } = useSelector(
    (state) =>
      state.offenses || {
        loading: false,
        offenses: [],
        pagination: { page: 1, limit: 10, totalRecords: 0, totalPages: 0 },
        uploadProgress: 0,
      },
  );

  const { districts, municipalities } = useSelector(
    (state) => state.internal || { districts: [], municipalities: [] },
  );

  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const limitOptions = [10, 20, 50, 100, 200, 500];
  const [filters, setFilters] = useState({
    district_id: "",
    municipality_id: "",
    search: "",
  });
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingOffense, setEditingOffense] = useState(null);
  const [viewMode, setViewMode] = useState("table");
  const [formData, setFormData] = useState({
    district_id: "",
    municipality_id: "",
    name: "",
    description: "",
    fine_amount: "",
  });

  useEffect(() => {
    dispatch(fetchOffenses(currentPage, limit, filters));
  }, [
    dispatch,
    currentPage,
    limit,
    filters.district_id,
    filters.municipality_id,
    filters.search,
  ]);

  useEffect(() => {
    dispatch(fetchDistricts());
  }, [dispatch]);

  useEffect(() => {
    if (filters.district_id) {
      dispatch(fetchMunicipalities(filters.district_id));
    } else {
      dispatch(clearMunicipalities());
    }
  }, [filters.district_id, dispatch]);

  useEffect(() => {
    if (formData.district_id) {
      dispatch(fetchMunicipalities(formData.district_id));
    } else {
      dispatch(clearMunicipalities());
    }
  }, [formData.district_id, dispatch]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
      ];
      if (!validTypes.includes(file.type)) {
        toast.error("Please upload an Excel file (.xlsx or .xls)");
        setSelectedFile(null);
        e.target.value = "";
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select a file first");
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const result = await dispatch(uploadOffense(formData));
      if (result?.meta?.status) {
        toast.success(result.meta.message || "Offenses uploaded successfully!");
        setSelectedFile(null);
        document.getElementById("fileInput").value = "";
        setShowUploadModal(false);
        dispatch(fetchOffenses(currentPage, limit, filters));
      } else {
        toast.error(result?.meta?.message || "Upload failed");
      }
    } catch (error) {
      toast.error(error.message || "Upload failed");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadTemplate = () => {
    dispatch(downloadTemplate());
  };

  const handleExportToExcel = async () => {
    try {
      const toastId = toast.loading("Preparing Offense Data Excel...");

      const result = await dispatch(fetchOffenses(1, 99999, filters));

      if (!result?.data || result.data.length === 0) {
        toast.error("No data to export");
        toast.dismiss(toastId);
        return;
      }

      const allOffenses = result.data;

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Offense Master");

      const columns = [
        { header: "S.N.", width: 8 },
        { header: "Offense Name", width: 30 },
        { header: "Description", width: 40 },
        { header: "Fine Amount", width: 15 },
        { header: "District", width: 25 },
        { header: "Municipality", width: 25 },
        { header: "Date", width: 20 },
      ];

      const headerRow = worksheet.addRow(columns.map((col) => col.header));
      headerRow.height = 25;
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 12 };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFCC6633" },
        };
        cell.alignment = {
          vertical: "middle",
          horizontal: "center",
          wrapText: true,
        };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      allOffenses.forEach((item, index) => {
        const rowArray = [
          index + 1,
          item.name || "N/A",
          item.description || "N/A",
          item.fine_amount || 0,
          item.district_name || "N/A",
          item.municipality_name || "N/A",
          item.created_at
            ? new Date(item.created_at).toLocaleDateString("en-IN")
            : "N/A",
        ];
        const row = worksheet.addRow(rowArray);
        row.eachCell((cell) => {
          cell.alignment = { vertical: "middle", horizontal: "left" };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
          if (index % 2 === 1) {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFF0F8FF" },
            };
          } else {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFFFFFFF" },
            };
          }
        });
      });

      worksheet.columns = columns.map((col) => ({ width: col.width }));
      worksheet.views = [{ state: "frozen", ySplit: 1 }];

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const date = new Date();
      const dateStr = `${String(date.getDate()).padStart(2, "0")}-${String(date.getMonth() + 1).padStart(2, "0")}-${date.getFullYear()}`;
      saveAs(blob, `Offense_Master_${dateStr}.xlsx`);

      toast.success(`Exported ${allOffenses.length} records successfully!`, {
        id: toastId,
      });
    } catch (error) {
      toast.error("Failed to export data");
      console.error(error);
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setCurrentPage(page);
      dispatch(fetchOffenses(page, limit, filters));
    }
  };

  const handleRefresh = () => {
    dispatch(fetchOffenses(currentPage, limit, filters));
    toast.success("Data refreshed!");
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAddOffense = async (e) => {
    e.preventDefault();
    if (
      !formData.district_id ||
      !formData.municipality_id ||
      !formData.name ||
      !formData.fine_amount
    ) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      const result = await dispatch(createOffense(formData));
      if (result?.meta?.status) {
        toast.success("Offense created successfully!");
        setShowAddModal(false);
        resetForm();
        dispatch(fetchOffenses(currentPage, limit, filters));
      } else {
        toast.error(result?.meta?.message || "Failed to create offense");
      }
    } catch (error) {
      toast.error(error.message || "Failed to create offense");
    }
  };

  const handleEditOffense = (offense) => {
    setEditingOffense(offense);
    setFormData({
      district_id: offense.district_id,
      municipality_id: offense.municipality_id,
      name: offense.name,
      description: offense.description || "",
      fine_amount: offense.fine_amount,
    });
    setShowEditModal(true);
  };

  const handleUpdateOffense = async (e) => {
    e.preventDefault();
    if (
      !formData.district_id ||
      !formData.municipality_id ||
      !formData.name ||
      !formData.fine_amount
    ) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      const result = await dispatch(updateOffense(editingOffense.id, formData));
      if (result?.meta?.status) {
        toast.success("Offense updated successfully!");
        setShowEditModal(false);
        resetForm();
        dispatch(fetchOffenses(currentPage, limit, filters));
      } else {
        toast.error(result?.meta?.message || "Failed to update offense");
      }
    } catch (error) {
      toast.error(error.message || "Failed to update offense");
    }
  };

  const handleDeleteOffense = async (id) => {
    if (window.confirm("Are you sure you want to delete this offense?")) {
      try {
        const result = await dispatch(deleteOffense(id));
        if (result?.meta?.status) {
          toast.success("Offense deleted successfully!");
          dispatch(fetchOffenses(currentPage, limit, filters));
        } else {
          toast.error(result?.meta?.message || "Failed to delete offense");
        }
      } catch (error) {
        toast.error(error.message || "Failed to delete offense");
      }
    }
  };

  const resetForm = () => {
    setFormData({
      district_id: "",
      municipality_id: "",
      name: "",
      description: "",
      fine_amount: "",
    });
    setEditingOffense(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 p-4 md:p-6">
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-orange-200/30 dark:bg-orange-900/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-200/30 dark:bg-blue-900/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative backdrop-blur-xl bg-white/70 dark:bg-slate-800/70 rounded-2xl shadow-xl border border-white/20 dark:border-slate-700/50 p-4 md:p-5 mb-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-lg">
              <FiUsers className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-orange-600 to-orange-400 bg-clip-text text-transparent">
                Offense Master
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                Manage and upload offense data
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-base"
            >
              <FiUpload className="w-4 h-4" />
              <span className="hidden sm:inline">Upload</span>
            </button>
            <button
              onClick={handleDownloadTemplate}
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-base"
            >
              <FiDownload className="w-4 h-4" />
              <span className="hidden sm:inline">Template</span>
            </button> */}
            <button
              onClick={handleRefresh}
              className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-slate-700 hover:bg-slate-50 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 border border-slate-200 dark:border-slate-600 text-base"
            >
              <FiRefreshCw className="w-4 h-4" />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button
              onClick={handleExportToExcel}
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-base"
            >
              <FiDownload className="w-4 h-4" />
              <span className="hidden sm:inline">Export</span>
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-base"
            >
              <FiPlus className="w-4 h-4" />
              <span>Add Offense</span>
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {[
          {
            label: "Total Offenses",
            value: pagination.totalRecords || offenses.length || 0,
            icon: FiUsers,
            bgColor: "bg-orange-50 dark:bg-orange-900/20",
            borderColor: "border-orange-200 dark:border-orange-800",
            iconBg: "from-orange-500 to-orange-600",
          },
          {
            label: "Districts",
            value: new Set(offenses.map((o) => o.district_id)).size,
            icon: FiUsers,
            bgColor: "bg-blue-50 dark:bg-blue-900/20",
            borderColor: "border-blue-200 dark:border-blue-800",
            iconBg: "from-blue-500 to-blue-600",
          },
          {
            label: "Municipalities",
            value: new Set(offenses.map((o) => o.municipality_id)).size,
            icon: FiUsers,
            bgColor: "bg-purple-50 dark:bg-purple-900/20",
            borderColor: "border-purple-200 dark:border-purple-800",
            iconBg: "from-purple-500 to-purple-600",
          },
          {
            label: "Total Fine Amount",
            value: offenses.reduce(
              (acc, o) => acc + (parseFloat(o.fine_amount) || 0),
              0,
            ),
            icon: FiUsers,
            bgColor: "bg-emerald-50 dark:bg-emerald-900/20",
            borderColor: "border-emerald-200 dark:border-emerald-800",
            iconBg: "from-emerald-500 to-emerald-600",
          },
        ].map((stat, idx) => (
          <div
            key={idx}
            className={`${stat.bgColor} border ${stat.borderColor} rounded-xl shadow-sm p-3 hover:shadow-xl transition-all duration-300 transform hover:scale-105`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
                  {stat.label}
                </p>
                <p className="text-2xl font-bold text-slate-800 dark:text-white mt-0.5">
                  {stat.value}
                </p>
              </div>
              <div
                className={`p-2.5 bg-gradient-to-br ${stat.iconBg} rounded-xl shadow-lg`}
              >
                <stat.icon className="w-4 h-4 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="relative backdrop-blur-xl bg-white/70 dark:bg-slate-800/70 rounded-2xl shadow-xl border border-white/20 dark:border-slate-700/50 p-4">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3 mb-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full lg:w-auto">
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <select
                value={filters.district_id}
                onChange={(e) => {
                  const value = e.target.value;
                  setFilters({
                    district_id: value,
                    municipality_id: "",
                    search: filters.search,
                  });
                  setCurrentPage(1);
                }}
                className="px-4 py-3 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm text-slate-700 dark:text-white w-full sm:w-auto"
              >
                <option value="">All Districts</option>
                {districts.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
              <select
                value={filters.municipality_id}
                onChange={(e) => {
                  setFilters({
                    ...filters,
                    municipality_id: e.target.value,
                  });
                  setCurrentPage(1);
                }}
                disabled={!filters.district_id}
                className="px-4 py-3 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm text-slate-700 dark:text-white disabled:opacity-50 w-full sm:w-auto min-w-[180px]"
              >
                <option value="">All Municipalities</option>
                {municipalities.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
              {(filters.district_id ||
                filters.municipality_id ||
                filters.search) && (
                <button
                  onClick={() => {
                    setFilters({
                      district_id: "",
                      municipality_id: "",
                      search: "",
                    });
                    setCurrentPage(1);
                    dispatch(clearMunicipalities());
                  }}
                  className="flex items-center gap-1.5 px-4 py-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105 text-sm font-medium whitespace-nowrap"
                >
                  <FiX className="w-4 h-4" />
                  Clear
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 w-full lg:w-auto">
            <div className="flex items-center gap-2 bg-white dark:bg-slate-700 rounded-xl border border-slate-200 dark:border-slate-600 p-1">
              <button
                onClick={() => setViewMode("table")}
                className={`p-2 rounded-lg transition-all duration-200 ${
                  viewMode === "table"
                    ? "bg-orange-500 text-white shadow-md"
                    : "text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                }`}
              >
                <FiList className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2 rounded-lg transition-all duration-200 ${
                  viewMode === "grid"
                    ? "bg-orange-500 text-white shadow-md"
                    : "text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                }`}
              >
                <FiGrid className="w-4 h-4" />
              </button>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-600 dark:text-slate-400">
                Show:
              </label>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-4 py-3 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm text-slate-700 dark:text-white"
              >
                {limitOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="relative">
              <div className="w-12 h-12 border-4 border-orange-200 dark:border-orange-800 rounded-full"></div>
              <div className="absolute top-0 left-0 w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          </div>
        ) : offenses.length > 0 ? (
          <>
            <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700">
              {viewMode === "table" ? (
                <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700">
                  <table className="w-full">
                    <thead className="bg-slate-50 dark:bg-slate-700/50">
                      <tr>
                        <th className="text-center py-2.5 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                          #
                        </th>
                        <th className="text-center py-2.5 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                          Offense Name
                        </th>
                        <th className="text-center py-2.5 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                          Description
                        </th>
                        <th className="text-center py-2.5 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                          Fine Amount
                        </th>
                        <th className="text-center py-2.5 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                          District
                        </th>
                        <th className="text-center py-2.5 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                          Municipality
                        </th>
                        <th className="text-center py-2.5 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="text-center py-2.5 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                      {offenses.map((item, index) => (
                        <tr
                          key={item.id}
                          className="hover:bg-orange-50/50 dark:hover:bg-orange-900/20 transition-colors duration-150"
                        >
                          <td className="py-2.5 px-3 text-center text-sm text-slate-500 dark:text-slate-400">
                            {(pagination.page - 1) * limit + index + 1}
                          </td>
                          <td className="py-2.5 px-3 text-left text-sm font-medium text-slate-800 dark:text-white">
                            {item.name || "N/A"}
                          </td>
                          <td className="py-2.5 px-3 text-left text-sm italic text-slate-600 dark:text-slate-300">
                            "{item.description || "N/A"}"
                          </td>
                          <td className="py-2.5 px-3 text-center text-sm font-medium text-emerald-600 dark:text-emerald-400">
                            ₹{item.fine_amount || "0"}
                          </td>
                          <td className="py-2.5 px-3 text-center text-sm text-slate-600 dark:text-slate-300">
                            {item.district_name || "N/A"}
                          </td>
                          <td className="py-2.5 px-3 text-center text-sm text-slate-600 dark:text-slate-300">
                            {item.municipality_name || "N/A"}
                          </td>
                          <td className="py-2.5 px-3 text-center text-sm text-slate-600 dark:text-slate-300">
                            {item.created_at
                              ? new Date(item.created_at).toLocaleDateString(
                                  "en-IN",
                                )
                              : "N/A"}
                          </td>
                          <td className="py-2.5 px-3 text-center">
                            <div className="flex items-center justify-center gap-1.5">
                              <button
                                onClick={() => handleEditOffense(item)}
                                className="p-1.5 text-orange-600 hover:bg-orange-100 dark:text-orange-400 dark:hover:bg-orange-900/30 rounded-lg transition-all duration-200 hover:scale-110"
                                title="Edit"
                              >
                                <FiEdit2 className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => handleDeleteOffense(item.id)}
                                className="p-1.5 text-red-600 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/30 rounded-lg transition-all duration-200 hover:scale-110"
                                title="Delete"
                              >
                                <FiTrash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {offenses.map((item) => (
                    <div
                      key={item.id}
                      className="bg-white dark:bg-slate-700/50 rounded-xl shadow-lg border-2 border-orange-200 dark:border-orange-700 p-4 hover:shadow-xl transition-all duration-300"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold text-slate-800 dark:text-white text-lg">
                            #{item.id} - {item.name || "N/A"}
                          </h3>
                          <p className="text-sm text-pink-500 dark:text-pink-400 italic mt-1">
                            "{item.description || "No description"}"
                          </p>
                        </div>
                        <div className="flex gap-1.5 ml-2">
                          <button
                            onClick={() => handleEditOffense(item)}
                            className="p-1.5 text-orange-600 hover:bg-orange-100 dark:text-orange-400 dark:hover:bg-orange-900/30 rounded-lg transition-all duration-200 hover:scale-110"
                            title="Edit"
                          >
                            <FiEdit2 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handleDeleteOffense(item.id)}
                            className="p-1.5 text-red-600 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/30 rounded-lg transition-all duration-200 hover:scale-110"
                            title="Delete"
                          >
                            <FiTrash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-600">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-500 dark:text-slate-400">
                            Fine Amount
                          </span>
                          <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                            ₹{item.fine_amount || "0"}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-sm mt-1">
                          <span className="text-slate-500 dark:text-slate-400">
                            Location
                          </span>
                          <span className="text-slate-700 dark:text-slate-300">
                            {item.district_name || "N/A"} -{" "}
                            {item.municipality_name || "N/A"}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-sm mt-1">
                          <span className="text-slate-500 dark:text-slate-400">
                            Date
                          </span>
                          <span className="text-slate-700 dark:text-slate-300">
                            {item.created_at
                              ? new Date(item.created_at).toLocaleDateString(
                                  "en-IN",
                                )
                              : "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {pagination.totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between mt-4 pt-3 border-t border-slate-200 dark:border-slate-700 gap-3">
                <div className="text-sm text-slate-500 dark:text-slate-400">
                  Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                  {Math.min(
                    pagination.page * pagination.limit,
                    pagination.totalRecords,
                  )}{" "}
                  of {pagination.totalRecords} entries
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-xl bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-slate-600 dark:text-slate-300"
                  >
                    <FiChevronsLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-xl bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-slate-600 dark:text-slate-300"
                  >
                    <FiChevronLeft className="w-4 h-4" />
                  </button>

                  <div className="flex items-center gap-1.5">
                    {(() => {
                      const totalPages = pagination.totalPages;
                      const current = currentPage;
                      const pages = [];
                      const maxVisible = 5;

                      if (totalPages <= maxVisible) {
                        for (let i = 1; i <= totalPages; i++) {
                          pages.push(i);
                        }
                      } else {
                        if (current <= 3) {
                          pages.push(1, 2, 3, 4, "...", totalPages);
                        } else if (current >= totalPages - 2) {
                          pages.push(
                            1,
                            "...",
                            totalPages - 3,
                            totalPages - 2,
                            totalPages - 1,
                            totalPages,
                          );
                        } else {
                          pages.push(
                            1,
                            "...",
                            current - 1,
                            current,
                            current + 1,
                            "...",
                            totalPages,
                          );
                        }
                      }

                      return pages.map((page, index) =>
                        page === "..." ? (
                          <span
                            key={`ellipsis-${index}`}
                            className="px-2 text-slate-500 dark:text-slate-400"
                          >
                            …
                          </span>
                        ) : (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`px-3.5 py-1.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                              current === page
                                ? "bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-md shadow-orange-500/30"
                                : "bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300"
                            }`}
                          >
                            {page}
                          </button>
                        ),
                      );
                    })()}
                  </div>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === pagination.totalPages}
                    className="p-2 rounded-xl bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-slate-600 dark:text-slate-300"
                  >
                    <FiChevronRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handlePageChange(pagination.totalPages)}
                    disabled={currentPage === pagination.totalPages}
                    className="p-2 rounded-xl bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-slate-600 dark:text-slate-300"
                  >
                    <FiChevronsRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-3">
              <FiUsers className="w-10 h-10 text-slate-300 dark:text-slate-500" />
            </div>
            <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300">
              No offenses found
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Get started by adding your first offense
            </p>
          </div>
        )}
      </div>

      {/* Add Offense Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md">
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 max-w-md w-full mx-4 shadow-2xl animate-fadeIn">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-slate-800 dark:text-white">
                Add Offense
              </h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <FiX className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <form onSubmit={handleAddOffense} className="space-y-3.5">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  District *
                </label>
                <select
                  name="district_id"
                  value={formData.district_id}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-slate-700 dark:text-white transition-all duration-200"
                >
                  <option value="">Select District</option>
                  {districts.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Municipality *
                </label>
                <select
                  name="municipality_id"
                  value={formData.municipality_id}
                  onChange={handleInputChange}
                  required
                  disabled={!formData.district_id}
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-slate-700 dark:text-white disabled:opacity-50 transition-all duration-200"
                >
                  <option value="">Select Municipality</option>
                  {municipalities.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Offense Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-slate-700 dark:text-white transition-all duration-200"
                  placeholder="Enter offense name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-slate-700 dark:text-white transition-all duration-200"
                  placeholder="Enter description"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Fine Amount *
                </label>
                <input
                  type="number"
                  name="fine_amount"
                  value={formData.fine_amount}
                  onChange={handleInputChange}
                  required
                  min="0"
                  step="1"
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-slate-700 dark:text-white transition-all duration-200"
                  placeholder="Enter fine amount"
                />
              </div>
              <div className="flex justify-end gap-3 pt-3.5 border-t border-slate-200 dark:border-slate-700">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-600 transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
                >
                  Create Offense
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Offense Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md">
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 max-w-md w-full mx-4 shadow-2xl animate-fadeIn">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-slate-800 dark:text-white">
                Edit Offense
              </h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  resetForm();
                }}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <FiX className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <form onSubmit={handleUpdateOffense} className="space-y-3.5">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  District *
                </label>
                <select
                  name="district_id"
                  value={formData.district_id}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-slate-700 dark:text-white transition-all duration-200"
                >
                  <option value="">Select District</option>
                  {districts.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Municipality *
                </label>
                <select
                  name="municipality_id"
                  value={formData.municipality_id}
                  onChange={handleInputChange}
                  required
                  disabled={!formData.district_id}
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-slate-700 dark:text-white disabled:opacity-50 transition-all duration-200"
                >
                  <option value="">Select Municipality</option>
                  {municipalities.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Offense Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-slate-700 dark:text-white transition-all duration-200"
                  placeholder="Enter offense name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-slate-700 dark:text-white transition-all duration-200"
                  placeholder="Enter description"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Fine Amount *
                </label>
                <input
                  type="number"
                  name="fine_amount"
                  value={formData.fine_amount}
                  onChange={handleInputChange}
                  required
                  min="0"
                  step="1"
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-slate-700 dark:text-white transition-all duration-200"
                  placeholder="Enter fine amount"
                />
              </div>
              <div className="flex justify-end gap-3 pt-3.5 border-t border-slate-200 dark:border-slate-700">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-600 transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
                >
                  Update Offense
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md">
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 max-w-md w-full mx-4 shadow-2xl animate-fadeIn">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-slate-800 dark:text-white">
                Upload Offenses
              </h3>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedFile(null);
                  document.getElementById("fileInput").value = "";
                }}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <FiX className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl p-8 text-center hover:border-orange-500 dark:hover:border-orange-400 transition-all duration-300 bg-slate-50 dark:bg-slate-700/30">
              <input
                id="fileInput"
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
                disabled={isUploading || loading}
              />
              <label htmlFor="fileInput" className="cursor-pointer block">
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center mb-4">
                    <FiFile className="w-8 h-8 text-orange-500 dark:text-orange-400" />
                  </div>
                  <p className="text-slate-600 dark:text-slate-300 font-medium">
                    {selectedFile
                      ? selectedFile.name
                      : "Click to select Excel file"}
                  </p>
                  <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">
                    Supported formats: .xlsx, .xls
                  </p>
                  {selectedFile && (
                    <p className="text-sm text-orange-600 dark:text-orange-400 mt-2">
                      Size: {(selectedFile.size / 1024).toFixed(2)} KB
                    </p>
                  )}
                </div>
              </label>
            </div>

            {uploadProgress > 0 && uploadProgress < 100 && (
              <div className="mt-4">
                <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-orange-500 to-orange-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">
                  {uploadProgress}% uploaded
                </p>
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
              <button
                type="button"
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedFile(null);
                  document.getElementById("fileInput").value = "";
                }}
                className="px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-600 transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={!selectedFile || isUploading || loading}
                className="px-5 py-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUploading ? (
                  <>
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                    Uploading...
                  </>
                ) : (
                  "Upload"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OffenseMaster;
